import { useRef, useState } from "react";
import { Camera, X, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";
import { STATUS, fileUrl, uploadFile, errMsg, fmtDate } from "@/lib/api";
import { useT } from "@/i18n";

export function StatusBadge({ status, testId }) {
  const { t } = useT();
  const s = STATUS[status] || { cls: "bg-stone-100 text-stone-700 border-stone-200" };
  return <span data-testid={testId || `status-badge-${status}`} className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${s.cls}`}>{t(`status.${status}`)}</span>;
}

export function Steps({ steps, current }) {
  return (
    <ol className="flex flex-wrap gap-y-3" data-testid="progress-steps">
      {steps.map((label, i) => {
        const done = i < current, active = i === current;
        const dot = done ? "bg-forest border-forest text-white" : active ? "bg-terracotta border-terracotta text-white pulse-ring" : "border-craft text-ink/40";
        return (
          <li key={label} className="flex items-center gap-2 pr-4 text-xs font-medium">
            <span className={`grid h-6 w-6 place-items-center rounded-full border text-[11px] transition-colors ${dot}`}>{i + 1}</span>
            <span className={done || active ? "text-ink" : "text-ink/40"}>{label}</span>
          </li>
        );
      })}
    </ol>
  );
}

export function Photo({ id, className = "" }) {
  return <img src={fileUrl(id)} alt="foto" loading="lazy" className={`rounded-xl object-cover bg-stone-100 ${className}`} data-testid={`photo-${id}`} />;
}

export function Uploader({ value = [], onChange, label, capture = false, big = false }) {
  const { t } = useT();
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const ids = [];
      for (const f of files) ids.push((await uploadFile(f)).id);
      onChange([...value, ...ids]);
      toast.success(t("common.uploaded", { n: ids.length }));
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
    e.target.value = "";
  };
  return (
    <div>
      <input ref={ref} type="file" accept="image/*,application/pdf" multiple hidden onChange={pick} capture={capture ? "environment" : undefined} data-testid="file-input" />
      <button type="button" data-testid="upload-button" onClick={() => ref.current.click()} disabled={busy}
        className={big ? "pwa-btn bg-white text-ink" : "btn-secondary"}>
        {busy ? <Loader2 className="animate-spin" size={18} /> : <Camera size={18} />} {busy ? t("common.loading") : (label || t("common.uploadPhoto"))}
      </button>
      {value.length > 0 && (
        <div className="mt-3 grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="uploaded-files">
          {value.map((id) => (
            <div key={id} className="relative group">
              <Photo id={id} className="h-24 w-full" />
              <button type="button" onClick={() => onChange(value.filter((x) => x !== id))} className="absolute -top-2 -right-2 grid h-6 w-6 place-items-center rounded-full bg-ink text-white opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-file-${id}`}><X size={12} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const KIND_ICON = { payment: "€", status: "•", photo: "📷", note: "✎", material: "▤" };

export function EventFeed({ events, files = [] }) {
  const { t } = useT();
  if (!events?.length) return <p className="text-sm text-ink/50">{t("common.noUpdates")}</p>;
  return (
    <ol className="relative border-l border-craft pl-6 space-y-6" data-testid="event-feed">
      {events.slice().reverse().map((e) => (
        <li key={e.id} className="relative" data-testid={`event-${e.kind}`}>
          <span className="absolute -left-[31px] grid h-5 w-5 place-items-center rounded-full bg-white border border-craft text-[10px] font-mono">{KIND_ICON[e.kind] || "•"}</span>
          <div className="text-xs text-ink/50 font-mono">{fmtDate(e.created_at, true)} · {e.actor_name}</div>
          <div className="text-sm mt-0.5">{e.text}</div>
          {e.file_ids?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {e.file_ids.map((id) => {
                const f = files.find((x) => x.id === id);
                return f?.content_type === "application/pdf"
                  ? <a key={id} href={fileUrl(id)} target="_blank" rel="noreferrer" className="btn-secondary !py-1.5 !px-3 text-xs"><FileText size={14} /> {f.original_filename}</a>
                  : <Photo key={id} id={id} className="h-28 w-36" />;
              })}
            </div>
          )}
        </li>
      ))}
    </ol>
  );
}

export function Empty({ title, children }) {
  return (
    <div className="card-craft p-10 text-center" data-testid="empty-state">
      <h3 className="font-serif text-2xl font-semibold">{title}</h3>
      <div className="mt-2 text-sm text-ink/60">{children}</div>
    </div>
  );
}
